import React, { useCallback, useEffect, useMemo, useState } from "react";

import Stack from "veneer/Stack";
import TextInput from "veneer/TextInput";

import type { Media } from "cortex/ui/src/fleet/view/media-library/model";
import Notification from "cortex/ui/src/ui/notification";
import { configReducer } from "cortex/ui/src/util/array";
import { utcToLocal } from "cortex/ui/src/util/time";

import {
  type EditContentGroupField,
  type EditContentGroupFormState,
  type GetChildState,
} from "./sidebarTypes";

interface EditContentGroupFormProps {
  excludeFields?: ReadonlyArray<EditContentGroupField>;
  getState?: GetChildState;
  mediaEntry?: Partial<Media>;
  readOnlyFields?: ReadonlyArray<"name">;
}

type FieldConfig = Record<string, boolean>;

function getFormState(mediaEntry: Partial<Media>): EditContentGroupFormState {
  const { name, type, upload_date } = mediaEntry;

  return {
    name,
    type,
    uploadDate: upload_date,
  };
}

export function EditContentGroupForm({
  excludeFields = [],
  getState = () => {},
  mediaEntry = {},
  readOnlyFields = [],
}: EditContentGroupFormProps) {
  const [formState, setFormState] = useState(() => getFormState(mediaEntry));

  useEffect(() => {
    setFormState(getFormState(mediaEntry));
  }, [mediaEntry]);

  const exportState = useCallback(() => ({ ...formState }), [formState]);

  useEffect(() => {
    getState(EditContentGroupForm.name, exportState);
  }, [exportState, getState]);

  const exclude = useMemo(
    () => excludeFields.reduce(configReducer, {} as FieldConfig),
    [excludeFields],
  );

  const readOnly = useMemo(
    () => readOnlyFields.reduce(configReducer, {} as FieldConfig),
    [readOnlyFields],
  );

  const isReadOnly = (field: string) => Boolean(readOnly[field]);

  return (
    <Stack dataTestId="edit-content-group-form" gap={2}>
      <Notification />
      {!exclude.name && (
        <TextInput
          disabled={isReadOnly("name")}
          fullWidth
          label="Media Group Name"
          onChange={(value) =>
            setFormState((current) => ({
              ...current,
              name: value,
            }))
          }
          onKeyDown={(event) => {
            event.stopPropagation();
          }}
          placeholder="Name"
          required
          size="medium"
          value={formState.name ?? ""}
        />
      )}
      {!exclude.uploadDate && (
        <TextInput
          disabled
          fullWidth
          label="Media Group Created Date"
          onChange={() => {}}
          onKeyDown={(event) => {
            event.stopPropagation();
          }}
          size="medium"
          value={utcToLocal(formState.uploadDate)}
        />
      )}
    </Stack>
  );
}
